import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { useCommandPaletteIndex } from '@/hooks/useCommandPaletteIndex';
import { useAccountRole } from '@/hooks/useAccountRole';

type StaticPaletteItem = { id: string; labelKey: string; path: string; keywords?: string; source: 'static' };

type DynamicPaletteItem = {
  id: string;
  label: string;
  path: string;
  keywords?: string;
  subtitle?: string;
  source: 'index';
};

type PaletteItem = StaticPaletteItem | DynamicPaletteItem;

const STATIC_ITEMS: StaticPaletteItem[] = [
  { id: 'home', labelKey: 'commandPalette.items.home', path: '/', keywords: 'dashboard start', source: 'static' },
  { id: 'sessions', labelKey: 'commandPalette.items.sessions', path: '/tabs', keywords: 'workspaces tabs', source: 'static' },
  { id: 'troves', labelKey: 'commandPalette.items.troves', path: '/troves', keywords: 'bookmarks links saved', source: 'static' },
  { id: 'notes', labelKey: 'commandPalette.items.notes', path: '/notes', keywords: 'docs documents', source: 'static' },
  { id: 'highlighter', labelKey: 'commandPalette.items.highlighter', path: '/highlighter', keywords: 'highlight clips', source: 'static' },
  { id: 'tasks', labelKey: 'commandPalette.items.tasks', path: '/tasks', keywords: '', source: 'static' },
  { id: 'assistant', labelKey: 'commandPalette.items.assistant', path: '/assistant', keywords: 'ai library', source: 'static' },
  { id: 'shared', labelKey: 'commandPalette.items.sharedWithMe', path: '/shared-with-me', keywords: 'sharing', source: 'static' },
  {
    id: 'session-context',
    labelKey: 'commandPalette.items.sessionContext',
    path: '/settings/profile?tab=browsing',
    keywords: 'session context automation smart categorize',
    source: 'static',
  },
  {
    id: 'mcp',
    labelKey: 'nav.mcp',
    path: '/settings/profile?tab=mcp#api-keys',
    keywords: 'api keys mcp cursor claude',
    source: 'static',
  },
  { id: 'settings-sub', labelKey: 'commandPalette.items.subscription', path: '/settings/subscription', keywords: 'billing plan', source: 'static' },
  { id: 'settings-vault', labelKey: 'commandPalette.items.vault', path: '/settings/vault', keywords: '', source: 'static' },
  { id: 'settings', labelKey: 'commandPalette.items.settings', path: '/settings/profile', keywords: 'preferences account profile', source: 'static' },
];

function matchesQuery(item: PaletteItem, q: string, labelText: string): boolean {
  if (!q) return true;
  const kw = (item.keywords ?? '').toLowerCase();
  return labelText.includes(q) || kw.includes(q) || item.id.includes(q);
}

export function CommandPalette() {
  const { t } = useTranslation('settings');
  const { canManageAccount } = useAccountRole();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dialogRootRef = useRef<HTMLDivElement | null>(null);
  const { items: indexedItems } = useCommandPaletteIndex(open);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  useDialogA11y(dialogRootRef, {
    isOpen: open,
    onClose: handleClose,
    ignoreEscape: false,
  });

  const allItems = useMemo((): PaletteItem[] => {
    const staticBase = canManageAccount
      ? STATIC_ITEMS
      : STATIC_ITEMS.filter((item) => item.id !== 'mcp');
    const dynamic: DynamicPaletteItem[] = indexedItems.map((row) => ({
      id: `${row.kind}-${row.id}`,
      label: row.label,
      path: row.path,
      keywords: row.keywords,
      subtitle: row.subtitle,
      source: 'index',
    }));
    return [...staticBase, ...dynamic];
  }, [canManageAccount, indexedItems]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((item) => {
      const labelText =
        item.source === 'static' ? t(item.labelKey).toLowerCase() : item.label.toLowerCase();
      return matchesQuery(item, q, labelText);
    });
  }, [allItems, query, t]);

  const onSelect = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
      setQuery('');
    },
    [navigate]
  );

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('palette') === '1') {
      setOpen(true);
      sp.delete('palette');
      const nextSearch = sp.toString();
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return null;

  return (
    <div
      ref={dialogRootRef}
      tabIndex={-1}
      className="fixed inset-0 z-[200] flex items-start justify-center bg-black/50 px-4 pt-[12vh] backdrop-blur-sm animate-in fade-in duration-150 focus:outline-none"
      role="dialog"
      aria-modal="true"
      aria-label={t('commandPalette.title')}
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) handleClose();
      }}
    >
      <div className="modal-surface w-full max-w-lg overflow-hidden border border-border bg-card shadow-modal animate-in zoom-in-95 duration-150">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            autoFocus
            className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={t('commandPalette.placeholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd className="hidden shrink-0 rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:inline">
            Esc
          </kbd>
        </div>
        <ul className="max-h-[min(50vh,320px)] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">{t('commandPalette.empty')}</li>
          ) : (
            filtered.map((item) => {
              const label = item.source === 'static' ? t(item.labelKey) : item.label;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col px-4 py-2.5 text-left transition-colors hover:bg-muted"
                    onClick={() => onSelect(item.path)}
                  >
                    <span className="text-sm font-semibold text-foreground">{label}</span>
                    {item.source === 'index' && item.subtitle ? (
                      <span className="text-xs text-muted-foreground">{item.subtitle}</span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
